import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BillingService } from '../../billing/billing/billing.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-detailed-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './detailed-report.component.html',
  styleUrl: './detailed-report.component.scss',
})
export class DetailedReportComponent implements OnInit {
  billingService = inject(BillingService);
  toastr = inject(ToastrService);
  Math = Math;

  reportData: any[] = [];
  allGroupedReport: any[] = [];
  groupedReport: any[] = []; // This will hold the paginated data
  expandedBills: Set<string> = new Set();
  isLoading = false;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  pageSizeOptions: number[] = [10, 20, 30, 40, 50, 100];

  selectedPreset: string = 'monthToDate';
  fromDate: string = '';
  toDate: string = '';

  totalBilled: number = 0;
  totalDiscounted: number = 0;
  totalCollected: number = 0;
  totalCostPrice: number = 0;
  totalCash: number = 0;
  totalCard: number = 0;
  totalUPI: number = 0;

  ngOnInit() {
    this.setRelative(this.selectedPreset);
    this.fetchReport();
  }

  setRelative(preset: string) {
    this.selectedPreset = preset;
    const today = new Date();
    let from = new Date();
    let to = new Date();

    switch (preset) {
      case 'today':
        break;
      case 'yesterday':
        from.setDate(today.getDate() - 1);
        to.setDate(today.getDate() - 1);
        break;
      case 'weekToDate':
        from.setDate(today.getDate() - today.getDay());
        break;
      case 'monthToDate':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'yearToDate':
        from = new Date(today.getFullYear(), 0, 1);
        break;
      case 'previousMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'previousYear':
        from = new Date(today.getFullYear() - 1, 0, 1);
        to = new Date(today.getFullYear() - 1, 11, 31);
        break;
    }

    this.fromDate = from.toISOString().split('T')[0];
    this.toDate = to.toISOString().split('T')[0];
  }

  onDateChange() {
    this.selectedPreset = '';
  }

  fetchReport() {
    this.isLoading = true;
    const payload: any = {};

    if (this.selectedPreset) {
      payload.preset = this.selectedPreset;
    } else {
      payload.fromDate = this.fromDate;
      payload.toDate = this.toDate;
    }

    this.billingService.getBillingReport(payload).subscribe({
      next: (data) => {
        this.reportData = data;
        this.calculateTotals();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastr.error('Failed to fetch report data');
        this.isLoading = false;
      },
    });
  }

  calculateTotals() {
    this.totalBilled = 0;
    this.totalDiscounted = 0;
    this.totalCollected = 0;
    this.totalCostPrice = 0;
    this.totalCash = 0;
    this.totalCard = 0;
    this.totalUPI = 0;

    const groups: Map<string, any> = new Map();
    const orderedGroups: any[] = [];

    this.reportData.forEach((item) => {
      const billNo = item.billingNumber;
      if (!groups.has(billNo)) {
        const newGroup = {
          billingNumber: billNo,
          date: item.date,
          customerName: item.customerName,
          cash: item.cash,
          card: item.card,
          upi: item.upi,
          finalAmount: 0,
          discount: 0,
          items: [],
        };
        groups.set(billNo, newGroup);
        orderedGroups.push(newGroup);
        // Sum bill-level payments only once per bill
        this.totalCash += Number(item.cash) || 0;
        this.totalCard += Number(item.card) || 0;
        this.totalUPI += Number(item.upi) || 0;
      }

      const billGroup = groups.get(billNo);
      billGroup.items.push(item);
      billGroup.finalAmount += Number(item.finalAmount) || 0;
      billGroup.discount += Number(item.discount) || 0;

      this.totalBilled += Number(item.finalAmount) || 0;
      this.totalDiscounted += Number(item.discount) || 0;
      this.totalCostPrice += Number(item.totalCost) || 0;
    });

    this.allGroupedReport = orderedGroups;
    this.totalCollected = this.totalCash + this.totalCard + this.totalUPI;

    this.currentPage = 1;
    this.updatePaginatedReport();

    // Auto-expand the first few bills for better UX
    if (this.groupedReport.length > 0) {
      this.expandedBills.add(this.groupedReport[0].billingNumber);
    }
  }

  updatePaginatedReport() {
    this.totalPages = Math.ceil(this.allGroupedReport.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.groupedReport = this.allGroupedReport.slice(startIndex, endIndex);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedReport();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedReport();
    }
  }

  goToPage(page: number | string) {
    if (typeof page === 'number') {
      this.currentPage = page;
      this.updatePaginatedReport();
    }
  }

  onPageSizeChange() {
    this.currentPage = 1;
    this.updatePaginatedReport();
  }

  getPages(): (number | string)[] {
    const pages: (number | string)[] = [];
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 2; // Number of pages to show around current page

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > delta + 2) pages.push('...');

      const start = Math.max(2, current - delta);
      const end = Math.min(total - 1, current + delta);

      for (let i = start; i <= end; i++) pages.push(i);

      if (current < total - delta - 1) pages.push('...');
      pages.push(total);
    }
    return pages;
  }

  toggleBill(billNo: string) {
    if (this.expandedBills.has(billNo)) {
      this.expandedBills.delete(billNo);
    } else {
      this.expandedBills.add(billNo);
    }
  }

  formatCurrency(amount: any) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(Number(amount) || 0);
  }

  formatDate(date: string) {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid Date';
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
